import { ReactNode } from 'react';

import { CSSProp } from 'styled-components';
import { Body, CloseButton } from '#/components/Modal';
import {
  FooterContainer,
  HeaderContainer,
  ModalHeaderInfo,
  ModalHeaderTitle,
  StyledModal,
  TopRightContainer,
} from './styles';
import type { ModalProps } from '#/components/Modal/Modal';

export interface HeadedModalProps extends Omit<ModalProps, 'title'> {
  /**
   * modal visibility state
   */
  readonly isOpen: boolean;
  /**
   * header title
   */
  readonly title: string | ReactNode;
  /**
   * title position
   */
  readonly titlePlacement?: 'center' | 'left';
  /**
   * title css
   */
  readonly titleCss?: CSSProp;
  /**
   * content placed inside body
   */
  readonly children: ReactNode;
  /**
   * Modal width
   */
  readonly width?: number | string;
  /**
   * Left info component placed inside header
   */
  readonly leftInfo?: ReactNode;
  /**
   * Right info component placed inside header
   */
  readonly rightInfo?: ReactNode;
  /**
   * on close handler, should set visibility to hidden
   */
  readonly close: () => void;
  /**
   * Color theme applied to modal
   */
  readonly theme?:
    | 'default'
    | 'primary-light'
    | 'secondary-light'
    | 'primary-dark';
  /**
   * Header css
   */
  readonly headerCss?: CSSProp;
  /**
   * component displayed inside footer container
   */
  readonly footerContent?: ReactNode;
  /**
   * Footer css
   */
  readonly footerCss?: CSSProp;
}

export default function HeadedModal({
  isOpen,
  close,
  leftInfo,
  rightInfo,
  title,
  children,
  headerCss,
  footerCss,
  titleCss,
  footerContent,
  titlePlacement = 'center',
  theme = 'default',
  ...props
}: HeadedModalProps) {
  const titleComponent = (
    <ModalHeaderTitle titlePlacement={titlePlacement} css={titleCss}>
      {title}
    </ModalHeaderTitle>
  );
  const header = (
    <HeaderContainer theme={theme} css={headerCss}>
      {titlePlacement === 'left' ? (
        <>
          {titleComponent}
          <div />
        </>
      ) : (
        <>
          <ModalHeaderInfo>{leftInfo}</ModalHeaderInfo>
          {titleComponent}
        </>
      )}
      <TopRightContainer>
        {rightInfo}
        <CloseButton aria-label="close modal" onClick={close} />
      </TopRightContainer>
    </HeaderContainer>
  );

  return (
    <StyledModal {...props} isOpen={isOpen} toggle={close} theme={theme}>
      {header}
      <Body>{children}</Body>
      {footerContent != null && (
        <FooterContainer theme={theme} css={footerCss}>
          {footerContent}
        </FooterContainer>
      )}
    </StyledModal>
  );
}
